import * as z from 'zod'
import {DoseCalcModeActive, VolumeMode} from '@cypro/engine/database/schema'

export const AgeRangeSchema = z.object({
  minAge: z.number().min(0),
  maxAge: z.number().min(0),
  maxDose: z.number().min(0),
})

export const MedicineSchema = z.object({
  id: z.string().optional(),
  activeSubstanceId: z.array(z.string()),
  prescribedNameId: z.array(z.string()),
  autIdem: z.boolean().optional(),
  calculationMethod: z.array(DoseCalcModeActive),
  notes: z.string().optional(),
  // cleared inputs are null (allowEmpty), so a line cannot be saved without them (CY-193)
  regularDose: z.number({error: 'Regular dose is required'}),
  precisionDecimals: z.int({error: 'Precision is required'}).min(0),
  doseProportion: z.number().min(0).max(100).optional(),
  ageRanges: z.array(AgeRangeSchema).optional(),
})

export type MedicineSchema = z.infer<typeof MedicineSchema>

export const TherapySequenceItemSchema = z
  .object({
    id: z.string(),
    day: z.number().min(0),
    time: z
      .string()
      .min(0)
      .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:MM format (00:00 to 23:59)'),
    isNote: z.boolean(),
    note: z.string().optional(),
    carrierSolutionId: z.array(z.string()),
    applicationFormId: z.array(z.string()),
    duration: z.number(),
    volume: z.number(),
    volumeMode: z.array(VolumeMode),
    medicine: z.array(MedicineSchema),
    requireCarrierSolution: z.boolean().optional(),
    dayZeroProtocol: z.boolean().optional(),
    usePrescribedNames: z.boolean().optional(),
    autIdem: z.boolean().optional(),
    therapyNote: z.string().optional(),
    pharmacyNote: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (!value.dayZeroProtocol) {
      if (value.day < 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['day'],
          message: 'Day cannot be less then 1',
        })
      }
    }

    if (value.isNote && (!value.note || value.note.trim().length === 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['note'],
        message: 'Notes are required for instruction notes',
      })
    }

    if (!value.isNote) {
      if (value.applicationFormId.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['applicationFormId'],
          message: 'At least one application form is required',
        })
      }
      if (
        value.applicationFormId.length !== 0 &&
        value.requireCarrierSolution &&
        value.carrierSolutionId.length === 0
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['carrierSolutionId'],
          message: 'A carrier solution is required',
        })
      }
      if (value.medicine.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['medicine'],
          message: 'At least one medicine is required',
        })
      }
      value.medicine.forEach((med, index) => {
        if (med.activeSubstanceId.length === 0 && med.prescribedNameId.length === 0) {
          ctx.addIssue({
            code: 'custom',
            path: [`medicine[${index}].activeSubstanceId`],
            message: 'Either an active substance or prescribed name is required',
          })
        }
        if (med.regularDose === 0) {
          ctx.addIssue({
            code: 'custom',
            path: [`medicine[${index}].regularDose`],
            message: 'Regular dose must be greater than 0',
          })
        }
        if (med.calculationMethod.length === 0) {
          ctx.addIssue({
            code: 'custom',
            path: [`medicine[${index}].calculationMethod`],
            message: 'At least one calculation method is required',
          })
        }
      })
    }

    if (
      !value.isNote &&
      value.applicationFormId.length !== 0 &&
      value.requireCarrierSolution &&
      value.carrierSolutionId.length === 0
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['carrierSolutionId'],
        message: 'A carrier solution is required',
      })
    }

    if (
      !value.isNote &&
      value.carrierSolutionId.length !== 0 &&
      value.requireCarrierSolution &&
      value.volumeMode.length === 0
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['volumeMode'],
        message: 'A volume mode is required',
      })
    }

    if (!value.isNote && value.carrierSolutionId.length !== 0 && value.volumeMode.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['volumeMode'],
        message: 'A volume mode is required',
      })
    }

    if (!value.isNote && value.carrierSolutionId.length !== 0 && value.volume <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['volume'],
        message: 'Volume is required',
      })
    }
  })

export type TherapySequenceItemSchema = z.infer<typeof TherapySequenceItemSchema>

export const ProtocolFormSchema = z.object({
  name: z.string().trim().min(1),
  stage: z.array(z.string()),
  cancerTypeId: z.array(z.string()),
  diagnosisId: z.array(z.string()),
  isTrial: z.boolean(),
  notes: z.string(),
  totalCycles: z.number().min(1).optional(),
  daysRepetition: z.number().min(1).optional(),
  therapySequence: z.array(TherapySequenceItemSchema).min(1, 'At least one therapy sequence entry is required'),
})

export type ProtocolFormSchema = z.infer<typeof ProtocolFormSchema>
